import * as Sentry from '@sentry/nextjs';
import Router from 'next/router';
import { gaTrackingIdForBrandSlug, isMockingEnvEnabled } from 'constant';
import { isClient } from 'utils/common';
import { brandSlugFromRoute } from 'utils/brandSlug';
import { v4 as uuidv4, validate } from 'uuid';
import axiosInstance from 'config/request';
import endpoints from '../config/endpoints';
import { TRACKING } from 'constant/analytics';

(function () {
  initSessionId();
})();

function initSessionId() {
  if (isClient()) {
    const sessionId = uuidv4();
    sessionStorage.setItem('sessionId', sessionId);
    return sessionId;
  }
}

export function getSessionId() {
  return sessionStorage.getItem('sessionId') || initSessionId();
}

// Every gtag call must name the property `_document` actually loaded for this page, or a moved
// merchant's page views would be configured on — and its client id read from — a property that was
// never initialised on the page.
const currentTrackingId = (): string | undefined => gaTrackingIdForBrandSlug(getBrandSlug());

export const pageview = (url: string) => {
  if (window.gtag) {
    window.gtag('config', currentTrackingId() as string, {
      page_path: url,
    });
  }
};

interface EventProps {
  action: string;
  category?: string;
  label?: string;
  value?: string | undefined | null;
  [key: string]: any;
}

function initClientId() {
  if (isClient()) {
    // e.g., 1234567890.1690000000, this is required format from GA
    let clientId = `${Math.floor(Math.random() * 1e10)}.${Date.now()}`;
    window.gtag('get', currentTrackingId(), 'client_id', (gaClientId: any) => {
      if (gaClientId && typeof gaClientId === 'string') clientId = gaClientId;
    });
    sessionStorage.setItem('clientId', clientId);
    return clientId;
  }
}

export function getGAClientId() {
  return sessionStorage.getItem('clientId') || initClientId();
}

// The merchant whose page produced the event. The backend uses it to decide which GA4 Analytics
// Property records the event (GEN-23787); it is routing metadata, not an event parameter.
export function getBrandSlug(): string | undefined {
  if (!isClient()) return undefined;
  try {
    return brandSlugFromRoute(Router.pathname, Router.query, Router.asPath);
  } catch {
    // `next/router`'s singleton throws "No router instance found" if a URL field is read before the
    // router initialises. This runs inside `event()`'s try, so letting it escape would swallow the
    // whole analytics send — losing the event entirely to save a routing hint.
    return undefined;
  }
}

export const event = ({ action, category, label, value, ...rest }: EventProps) => {
  // For local testing purposes only, enable analytics sending
  if (isMockingEnvEnabled) {
    return;
  }

  try {
    const time = new Date();

    const valueArray = typeof value === 'string' && value?.match(/.{1,100}/g);
    const values =
      Array.isArray(valueArray) &&
      valueArray?.reduce((obj, currString, index) => {
        const key = `customValue${index ? index : ''}`;
        return {
          ...obj,
          [key]: currString,
        };
      }, {});

    // Process additional parameters (from ...rest) to handle GA character limits
    // Similar to how we chunk 'value' and 'pageLocation' above, this function splits
    // long string values (>100 chars) into multiple parameters to comply with GA constraints.
    // It safely returns an empty object on failure to ensure other event() calls aren't affected.
    const processedRest = processRestForGA(rest);

    const pageLocation = isClient() ? window.location.href : '';
    const pageLocationsArray = typeof pageLocation === 'string' && pageLocation?.match(/.{1,100}/g);
    const pageLocations =
      Array.isArray(pageLocationsArray) &&
      pageLocationsArray?.reduce((obj, currString, index) => {
        const key = `customPageLocation${index ? index : ''}`;
        return {
          ...obj,
          [key]: currString,
        };
      }, {});

    const sessionId = getSessionId();
    const uuid = uuidv4();
    const customTime = time.toISOString();

    const eventParams = {
      event_category: category,
      event_label: label,
      value: value,
      uuid: uuid,
      sessionId,
      customLabel: label,
      customAction: action,
      customTime: customTime,
      ...values,
      ...pageLocations,
      ...processedRest,
      // debug_mode: true, // Enable debug mode for GA4
    };
    if (!validate(uuid) || eventParams.customTime === 'other') {
      Sentry?.captureException?.(eventParams);
    }

    // Always send to custom analytics endpoint
    axiosInstance
      .post(`${endpoints.momos.survey.analytics}`, {
        clientId: getGAClientId(),
        action: action,
        eventParams,
        brandSlug: getBrandSlug(),
      })
      .then()
      .catch(() => {
        console.log('Failed to send analytics to custom endpoint', getSessionId());
      });
  } catch (err) {
    console.log('Failed to send analytics', getSessionId());
    Sentry?.captureException?.(err);
  }
};

/**
 * Processes additional parameters for Google Analytics by splitting long string values into chunks.
 *
 * Google Analytics has limitations on parameter value length. This function splits string values
 * longer than 100 characters into multiple parameters to comply with GA's constraints.
 *
 * @param rest - A record of key-value pairs containing additional parameters to process
 * @returns A processed record where:
 *   - String values longer than 100 characters are split into chunks
 *   - The first chunk retains the original key
 *   - Subsequent chunks are stored with numbered suffixes (e.g., `key1`, `key2`, etc.)
 *   - Non-string values are preserved as-is
 *
 * @example
 * ```typescript
 * const params = {
 *   description: 'a'.repeat(250), // 250 characters
 *   count: 42
 * };
 *
 * const processed = processRestForGA(params);
 * // Result:
 * // {
 * //   description: 'aaa...aaa' (first 100 chars),
 * //   description1: 'aaa...aaa' (next 100 chars),
 * //   description2: 'aaa...aaa' (remaining 50 chars),
 * //   count: 42
 * // }
 * ```
 */
function processRestForGA(rest: Record<string, any>) {
  try {
    const processed: Record<string, any> = {};

    Object.entries(rest).forEach(([key, value]) => {
      if (typeof value === 'string') {
        const chunks = value.match(/.{1,100}/g);
        if (chunks && chunks.length > 0) {
          processed[key] = chunks[0];
          chunks.slice(1).forEach((chunk, idx) => {
            processed[`${key}${idx + 1}`] = chunk;
          });
        } else {
          processed[key] = value;
        }
      } else {
        processed[key] = value;
      }
    });

    return processed;
  } catch (error) {
    // Safely return empty object on failure to ensure we don't affect other calls to event() function
    console.error('Failed to process parameters for GA:', error);
    return {};
  }
}

/**
 * Tracks survey submission data to Google Analytics with automatic chunking for large payloads.
 *
 * Google Analytics has a limit of 25 parameters per event. This function handles large survey
 * submission payloads by:
 * 1. Stringifying the payload and processing it through `processRestForGA` to split long strings and send to custom analytics endpoint
 * 2. Chunking the resulting parameters into multiple events if they exceed the available slots
 * 3. Adding metadata (chunk_index, total_chunks) to each event for reconstruction
 *
 * The function estimates that default parameters (from TRACKING.SURVEY.BUTTON.SUBMIT) use
 * approximately 15 slots, leaving 10 slots available for dynamic survey data.
 *
 * @param surveySubmissionPayload - The survey submission data object to track. This will be
 *   stringified and may be split across multiple GA events if it's too large.
 *
 * @example
 * ```typescript
 * const surveyData = {
 *   answers: { q1: 'answer1', q2: 'answer2' },
 *   metadata: { timestamp: '2024-01-01', userId: '123' }
 * };
 *
 * trackGASubmission(surveyData);
 * // May send multiple GA events if the stringified payload is large:
 * // Event 1: { ...defaultParams, survey_submit_value: '{"answers":{...', chunk_index: 1, total_chunks: 2 }
 * // Event 2: { ...defaultParams, survey_submit_value1: '..."metadata":{...', chunk_index: 2, total_chunks: 2 }
 * ```
 *
 * @remarks
 * - Each event includes `chunk_index` (1-based) and `total_chunks` for tracking which part
 *   of the payload this event represents
 * - If the payload is small enough, only one event will be sent
 * - The function uses the `event` function internally, which handles both GA4 and custom analytics endpoints
 */
export const trackGASubmission = (surveySubmissionPayload: any) => {
  try {
    if (isMockingEnvEnabled) {
      return;
    }
    const GA_PARAMS_LIMIT = 25;
    const allParams = {
      survey_submit_value: JSON.stringify(surveySubmissionPayload),
    };

    const processedDynamicParams = processRestForGA(allParams);
    const dynamicParamKeys = Object.keys(processedDynamicParams);

    const ESTIMATED_DEFAULT_PARAMS_COUNT = 15; // Assuming default params take up about 15 slots
    const AVAILABLE_SLOTS_FOR_DYNAMIC = GA_PARAMS_LIMIT - ESTIMATED_DEFAULT_PARAMS_COUNT;

    const chunkSize = Math.max(1, AVAILABLE_SLOTS_FOR_DYNAMIC);

    for (let i = 0; i < dynamicParamKeys.length; i += chunkSize) {
      const chunkKeys = dynamicParamKeys.slice(i, i + chunkSize);

      const paramsChunk = chunkKeys.reduce((acc, key) => {
        acc[key] = processedDynamicParams[key];
        return acc;
      }, {} as Record<string, any>);

      paramsChunk['chunk_index'] = Math.floor(i / chunkSize) + 1;
      paramsChunk['total_chunks'] = Math.ceil(dynamicParamKeys.length / chunkSize);

      event({
        ...TRACKING.SURVEY.SUBMIT,
        ...paramsChunk,
      });
    }
  } catch (error) {
    // Silently handle errors to prevent breaking the application flow
    // This function is used in critical paths and must not throw
    console.error('Failed to track GA submission:', error);
    Sentry?.captureException?.(error);
  }
};
